import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { IToolInfoUpdate } from '@account-users/account/models/IToolInfo';
import { LicenseCode } from '@account-users/account/models/LicenseCode';
import { Permission } from '@account-users/account/models/Permission';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import {
  LicensePermissionList,
  UserLicencePermission,
} from '@account-users/account/models/LicensePermissionList';
import { License } from '@account-users/account/models/License';
import { LicenseTypeConfig } from '@account-users/account/models/LicenseTypeConfig';
import { AccountLicense } from '@account-users/account/models/AccountLicense';
import { getAllPermissionsExceptCore } from '@account-users/utils';
import { AccountState } from '@account-users/account/models/IAccount';
import { ISection } from '@account-users/account/models/Audience';

@Component({
  selector: 'app-edit-permissions',
  templateUrl: './edit-permissions.component.html',
  styleUrls: ['./edit-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditPermissionsComponent {
  @Input() accountLicenses: AccountLicense[] = [];
  @Input() user: IAccountUser;
  @Input() isUpdatingLicenseTool: boolean;
  @Input() isUpdatingSections: boolean;
  @Input() canTogglePermissions: boolean;
  @Input() licensePermissions: LicensePermissionList[] = [];
  @Input() licensePermissionsForAccountUser: UserLicencePermission[] = [];
  @Input() allLicenses: License[] = [];
  @Input() allSections: ISection[] = [];
  @Input() licenseTypes: LicenseTypeConfig;
  @Output() updatePermission = new EventEmitter<IToolInfoUpdate>();
  @Output() updateSectionPermission = new EventEmitter<any>();

  LicenseCode = LicenseCode;
  Permission = Permission;

  get coreLicensePermissions(): LicensePermissionList {
    if (this.licensePermissions) {
      return this.licensePermissions.find(
        (licensePermission) => licensePermission.code === LicenseCode.Core,
      );
    } else {
      return {
        code: LicenseCode.Core,
        permissions: [],
      } as LicensePermissionList;
    }
  }

  get allLicensesExceptCore() {
    return getAllPermissionsExceptCore(this.licensePermissions).filter(
      (lp) => this.getLicenseType(lp.code) !== 'core',
    );
  }

  get coreLicenses() {
    return getAllPermissionsExceptCore(this.licensePermissions).filter(
      (lp) => this.getLicenseType(lp.code) === 'core',
    );
  }

  hasLicense(licenseCode: LicenseCode) {
    return this.accountLicenses.some((al) => al.licenseCode === licenseCode);
  }

  isLicenseStateSuccess(licenseCode) {
    const al = this.accountLicenses.find((l) => l.licenseCode === licenseCode);
    return al && al.state === AccountState.Success;
  }

  onToolChange(toolId: LicenseCode, toUpdate: string, checked: boolean) {
    this.updatePermission.emit({ toolId, toUpdate, checked });
  }

  onSectionChange(toggle, sectionId) {
    this.updateSectionPermission.emit({ toggle, sectionId });
  }

  getDisplayName(licenseCode: LicenseCode) {
    return this.allLicenses.find((tool) => tool.code === licenseCode)
      .displayName;
  }

  getLicenseType(licenseCode: LicenseCode) {
    return this.allLicenses.find((tool) => tool.code === licenseCode).type;
  }

  isEditable(license: UserLicencePermission, permission: string): boolean {
    const licensePermission = this.licensePermissionsForAccountUser.find(
      (lp) => lp.code === license.code,
    );
    const permissionForLicense =
      licensePermission &&
      licensePermission.permissions.find((p) => p.permission === permission);

    return permissionForLicense
      ? permissionForLicense.editable
      : this.hasLicense(license.code);
  }

  getToolTipText(license: UserLicencePermission) {
    return this.hasLicense(license.code)
      ? 'Disabled due to role'
      : 'Tool is not licensed on account';
  }
}
